import { useListCustomAttributes } from "#src/components/hooks/adapters/useCustomAttributes";
import useLocalization from "#src/hooks/useLocalization";
import {
  DropdownInput,
  FilterBuilder,
  FilterBuilderFieldDefinition,
  FilterBuilderFieldsType,
  Form,
  useForm,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  WorkflowTriggerAssetCreate,
  WorkflowTriggerAssetUpdate,
} from "@validereinc/domain";
import { isEqual, merge, pick } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

type FormDataType = Pick<
  WorkflowTriggerAssetCreate,
  "asset_filter" | "asset_type"
> &
  Partial<Pick<WorkflowTriggerAssetUpdate, "attributes">>;

const assetTypeToPrimaryFieldsMap: Partial<
  Record<AssetTypeType, FilterBuilderFieldsType>
> = {
  facility: {
    name: {
      display_name: "Name",
      data_type: "string",
    },
    status: {
      display_name: "Status",
      data_type: "pick-list",
      pick_list_options: ["active", "inactive"],
    },
  },
  equipment: {
    name: {
      display_name: "Name",
      data_type: "string",
    },
    status: {
      display_name: "Status",
      data_type: "pick-list",
      pick_list_options: ["active", "inactive", "deprecated"],
    },
  },
};

const WorkflowConfigurationAssetCreateUpdate = ({
  hasAttributesPicker,
  triggerIndex,
}: {
  hasAttributesPicker: boolean;
  triggerIndex: number;
}) => {
  const { setTriggerAsset, setTriggerAssetIsValid, getTriggerAsset } =
    useWorkflowConfigurationFormStore();
  const { localize } = useLocalization();

  const storeValues = getTriggerAsset();

  const form = useForm<FormDataType>({
    values: storeValues
      ? (pick(storeValues, [
          "asset_filter",
          "asset_type",
          ...(hasAttributesPicker ? ["attributes"] : []),
        ]) as FormDataType)
      : undefined, // Cast to FormDataType to resolve type issues
  });

  const assetType = form.watch("asset_type");

  const handleValueChange = (key: keyof FormDataType, value: unknown) => {
    if (storeValues) {
      setTriggerAsset({
        ...storeValues,
        [key]: value,
      });
    }
  };

  const [internalAssetFilter, setInternalAssetFilter] = useState({});

  useDeepCompareEffectNoCheck(() => {
    if (
      !isEqual(internalAssetFilter, storeValues?.asset_filter) &&
      storeValues?.asset_filter
    ) {
      setInternalAssetFilter(storeValues?.asset_filter ?? {});
    }
  }, []);

  const assetTypeOptions = useMemo(
    () =>
      Object.values(AssetType)
        .filter((assetType) =>
          (
            [AssetType.FACILITY, AssetType.EQUIPMENT] as AssetTypeType[]
          ).includes(assetType)
        )
        .map((value) => ({
          label: localize(value),
          value,
        })),
    [localize]
  );

  const { data: customAttributesData } = useListCustomAttributes(
    {
      filters: {
        entity_type: assetType,
      },
    },
    {
      enabled: Boolean(assetType),
    }
  );

  const fields: FilterBuilderFieldsType = useMemo(() => {
    if (!customAttributesData?.data) return {};

    const calculatedFields: FilterBuilderFieldsType = {};

    if (assetType && assetTypeToPrimaryFieldsMap[assetType]) {
      // es-toolkit `merge`, merges all other arguments into the first one (not immutable):
      merge(calculatedFields, assetTypeToPrimaryFieldsMap[assetType]);
    }

    customAttributesData.data.forEach((customAttribute) => {
      const key = `custom_attributes.${customAttribute.field_name}`;
      const { data_type, display_name } = customAttribute;

      let field: FilterBuilderFieldDefinition | undefined = undefined;

      if (
        data_type === "string" ||
        data_type === "boolean" ||
        data_type === "number"
      ) {
        field = {
          data_type,
          display_name,
        };
      }

      if (data_type === "integer") {
        field = {
          data_type: "number",
          display_name,
        };
      }

      if (data_type === "pick-list" || data_type === "multi-pick-list") {
        field = {
          data_type: "pick-list",
          display_name,
          pick_list_options: customAttribute.pick_list_values ?? [],
        };
      }

      if (field) {
        calculatedFields[key] = field;
      }
    });

    return calculatedFields;
  }, [customAttributesData]);

  // Write new filter to form values:
  useDeepCompareEffectNoCheck(() => {
    handleValueChange("asset_filter", internalAssetFilter);
  }, [internalAssetFilter]);

  useEffect(() => {
    setTriggerAssetIsValid(form.formState.isValid);
  }, [form.formState.isValid, setTriggerAssetIsValid]);

  useEffect(() => {
    setInternalAssetFilter(storeValues?.asset_filter ?? {});
  }, [triggerIndex]);

  const attributesOptions = useMemo(() => {
    if (!hasAttributesPicker) return [];

    return Object.keys(fields).map((fieldKey) => ({
      label: fields[fieldKey].display_name,
      value: fieldKey,
    }));
  }, [fields, hasAttributesPicker]);

  return (
    <>
      <Form {...form}>
        <DropdownInput
          label="Asset Type"
          options={assetTypeOptions}
          name="asset_type"
          labelKey="label"
          valueKey="value"
          isSearchable={false}
          isClearable={false}
          isRequired
          onChange={(newAssetType: AssetTypeType) => {
            if (storeValues) {
              setTriggerAsset({
                ...storeValues,
                asset_type: newAssetType,
                ...(newAssetType !== assetType ? { asset_filter: {} } : {}),
              });
            }

            if (newAssetType !== assetType) {
              setInternalAssetFilter({});
            }
          }}
        />
        {hasAttributesPicker && (
          <DropdownInput
            label="Attributes"
            options={attributesOptions}
            isDisabled={attributesOptions.length === 0}
            name="attributes"
            labelKey="label"
            valueKey="value"
            isMulti
            onChange={(newAttributes: string[]) => {
              handleValueChange("attributes", newAttributes);
            }}
          />
        )}
      </Form>
      <FilterBuilder
        filter={internalAssetFilter}
        setFilter={setInternalAssetFilter}
        fields={fields}
        maxDepth={2}
        title="Asset Filter"
        variant="vertical"
      />
    </>
  );
};

export default WorkflowConfigurationAssetCreateUpdate;
